import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/utils/navigationRoutes";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { SLA_CONNECTION_GUIDE_PDF } from "@/utils/constant/slaDocs";
import { BookOpen, Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface SLASchemaConfigRow {
  id?: string | number;
  data_source_type: string;
  schemas: { schema_type: string; table_name: string }[];
}

const SLASchemaConfigurationListPage = () => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["sla-schema-configurations"],
    queryFn: () => SLAConfigurationService.getSLASchemaConfiguration(),
  });

  // Normalise — API may return {count, results:[...]}, array, or single object
  const dataSource: SLASchemaConfigRow[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : data && typeof data === "object" && !data.results
        ? [data]
        : [];

  const openSlaConnectionGuide = () => {
    window.open(SLA_CONNECTION_GUIDE_PDF, "_blank", "noopener,noreferrer");
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await SLAConfigurationService.deleteConfiguration(id);
      notify.success("Deleted successfully.");
      refetch();
    } catch {
      notify.error("Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ProColumns<SLASchemaConfigRow>[] = [
    {
      title: "Data Source Type",
      dataIndex: "data_source_type",
      width: 180,
    },
    {
      title: "SLA Policy Schema",
      render: (_, record) =>
        record.schemas?.find(s => s.schema_type === "sla_policy")?.table_name ??
        "—",
    },
    {
      title: "Business Hours Schema",
      render: (_, record) =>
        record.schemas?.find(s => s.schema_type === "business_hours")
          ?.table_name ?? "—",
    },
    {
      title: "Holiday Calendar Schema",
      render: (_, record) =>
        record.schemas?.find(s => s.schema_type === "holiday_calendar")
          ?.table_name ?? "—",
    },
    {
      title: "Escalation Time Schema",
      render: (_, record) =>
        record.schemas?.find(s => s.schema_type === "escalation_time")
          ?.table_name ?? "—",
    },
    {
      title: "Main Apply Schema",
      render: (_, record) =>
        record.schemas?.find(s => s.schema_type === "applye_table")
          ?.table_name ?? "—",
    },
    {
      title: "Actions",
      width: 120,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit"
          className="!p-1 hover:!bg-muted"
          onClick={() => navigate(navigationRoutes.SLA_CONFIGURATION_ADD)}
        />,
        record.id ? (
          <Popconfirm
            key="delete"
            title="Delete this SLA schema configuration?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(String(record.id))}
            okText="Yes"
            cancelText="No"
          >
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              title="Delete"
              loading={deletingId === String(record.id)}
              className="!p-1"
            />
          </Popconfirm>
        ) : null,
      ],
    },
  ];

  const toolBarRender = () => [
    <CustomButton
      key="sla-guide"
      icon={<BookOpen size={16} />}
      onClick={openSlaConnectionGuide}
      title="Open SLA JSON schema connection guide (PDF)"
    >
      SLA Guide
    </CustomButton>,
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={() => navigate(navigationRoutes.SLA_CONFIGURATION_ADD)}
      title="New SLA Configuration"
    >
      New SLA
    </CustomButton>,
  ];

  return (
    <div className="sla-schema-config-list-page">
      <BaseTable<SLASchemaConfigRow>
        loading={isLoading}
        columns={columns}
        dataSource={dataSource}
        page={1}
        pageSize={dataSource.length || 10}
        total={dataSource.length}
        rowKey={(record, index) => String(record.id ?? index)}
        toolBarRender={toolBarRender}
        headerTitle="New Sla"
        tableName={gridName.slaConfigurationTable}
        request={() => refetch()}
      />
    </div>
  );
};

export default SLASchemaConfigurationListPage;
